import { request as umiRequest, history } from 'umi'
import { message } from 'antd';
import { setAuth, getAuth } from "./db";
import { Toast } from "antd-mobile";
import {getLocalItem} from "@/utils/tools";

export const post = { method: 'POST' }
export const lang = (navigator.language === 'en' ? 'en-US' : navigator.language) || 'en-US';

// 请求计数器
let requestCount = 0;

// 存储原始路径，避免重复跳转后丢失
let originalRedirectPath = '';

// 显示loading
const showLoading = () => {
    if (requestCount === 0) {
        Toast.show({
            content: 'loading...',
            icon: 'loading',
            duration: 0, // 0表示不自动关闭
        });
    }
    requestCount++;
};

// 隐藏loading
const hideLoading = () => {
    requestCount--;
    if (requestCount === 0) {
        Toast.clear();
    }
};

/**
 * http异常
 */
const HttpErrors = {
    401: '登录已过期，请重新登录',
    403: '无权访问',
    404: '地址不存在',
    405: '非法的接口请求类型',
    409: '账号已在其他设备登录',
    413: '文件过大，请更换文件后重试',
    502: '系统不可用，请稍后重试',
    503: '系统维护升级中，请稍后再进行访问，给您带来不便敬请谅解',
    504: '网关超时',
};

// 判断是否为首页或登录页
const isHomeOrLoginPage = () => {
    const pathname = window.location.pathname;
    return pathname === '/' ||
        pathname.includes('/user/login');
};

// 获取有效的重定向路径（只获取一次）
const getValidRedirectPath = () => {
    // 如果已经存储了原始路径，直接返回
    if (originalRedirectPath) {
        return originalRedirectPath;
    }

    const currentPath =  (window.location.pathname + window.location.search).replace(/^\/m/, '');

    // 排除登录页和首页
    if (currentPath.includes('/user/login') ||
        currentPath === '/') {
        return '';
    }

    // 存储原始路径
    originalRedirectPath = currentPath;
    return originalRedirectPath;
};

// 重置重定向路径（登录成功后调用）
export const resetRedirectPath = () => {
    originalRedirectPath = '';
};

// 需要跳转登录页的状态码
const LOGIN_REDIRECT_CODES = [401, 301, 302, 409];

// 处理登录跳转
let isRedirecting = false; // 防止重复跳转

const handleLoginRedirect = (status, msg) => {
    // 如果已经在跳转中，直接返回
    if (isRedirecting) {
        return;
    }

    // 如果是首页或登录页，只提示不跳转
    if (isHomeOrLoginPage()) {
        if (msg) {
            Toast.show(msg);
        }
        return;
    }

    // 设置跳转状态，防止重复跳转
    isRedirecting = true;

    // 非首页且非登录页，提示并跳转到登录页
    if (msg) {
        Toast.show(msg);
    }

    // 延迟跳转，确保用户能看到提示信息
    setTimeout(() => {
        // 清除认证信息
        setAuth('');

        // 获取有效的重定向路径（只获取一次）
        const redirectPath = getValidRedirectPath();
        const invite = getLocalItem('invite');

        if (redirectPath) {
            // 直接使用路径，不重复编码
            history.push(`/user/login?invite=${invite}&redirect=${redirectPath}`);
        } else {
            history.push(`/user/login?invite=${invite}`);
        }

        // 跳转完成后重置状态
        setTimeout(() => {
            isRedirecting = false;
        }, 2000);
    }, 300);
};


//TODO  status 为空时屏蔽错误信息，主要针对文件流接口，根据业务需求有待调整
const skipErrorMsg = ({ status, request, data, config }) => {
    if (status === 200 && (request?.responseType === 'blob' || config.skipErrorHandler)) {
        return true;
    }
    // return !data?.status || data?.status > 10000 || [200,2017,2015].includes(data?.status);
};

const local = localStorage.getItem('umi_locale') || lang;

export const requestConfig = {
    timeout: 60 * 1000,
    timeoutErrorMessage: '网络不稳定，连接超时了',
    headers: { 'lang': local },
    errorConfig: {
        // 错误抛出
        errorThrower: (res) => {
            const { success, status } = res;
            const msg = res.data?.msg;
            // 抛出自制的错误
            if (!success) {
                const error = new Error(msg);
                error.name = 'customError';
                error.info = { status, message: msg };
                throw error;
            }
        },
        // 错误接收及处理
        errorHandler: (error, opts) => {
            const { response, name, info, request } = error || {};
            if (opts?.skipErrorHandler && response?.status === 200) throw error;
            message.destroy('imageLoading');

            // 我们的 errorThrower 抛出的错误。
            if (name === 'customError') {
                const msg = info.data?.msg;
                Toast.show(msg);
            } else if (response) {
                // Axios 的错误
                // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
                if (LOGIN_REDIRECT_CODES.includes(response.status)) {
                    // 登录状态异常，跳转到登录页
                    handleLoginRedirect(response.status, HttpErrors[response.status]);
                } else {
                    Toast.show(HttpErrors[response.status] || '网络不稳定，请稍后再试');
                }
            } else if (error?.message?.includes('timeout')) {
                Toast.show('网络不稳定，连接超时了');
            }
        }
    },
    requestInterceptors: [
        // 直接写一个 function，作为拦截器
        (url, options) => {
            showLoading();
            return {
                url,
                options: {
                    ...options,
                    headers: {
                        ...options?.headers,
                        token: getAuth(),
                    },
                },
            }
        },
        // 一个二元组，第一个元素是 request 拦截器，第二个元素是错误处理
        [(url, options) => { return { url, options } }, (error) => { return Promise.reject(error) }],
        // 数组，省略错误处理
        [(url, options) => { return { url, options } }]
    ],
    responseInterceptors: [
        // 直接写一个 function，作为拦截器
        (response) => {
            hideLoading();
            const { data, headers } = response;
            const auth = data?.data?.token;

            // 处理业务状态码跳转逻辑
            if (LOGIN_REDIRECT_CODES.includes(data?.status)) {
                handleLoginRedirect(data.status, data.msg);
            }

            if (auth) {
                setAuth(auth);
            }

            data.success = skipErrorMsg(response);
            return {
                ...response,
                data
            };
        },
    ]
};

export const request = async (url, options) => {
    let res;
    try {
        res = await umiRequest(url, options);

        // 处理业务状态码跳转逻辑
        if (LOGIN_REDIRECT_CODES.includes(res?.status)) {
            handleLoginRedirect(res.status, res.msg);
        }

        if (res.status*1 !== 1 ) {
            Toast.show(res.msg || '操作异常');
        }
    } catch (e) {
        console.info('请求错误:', e);
        // 网络错误或HTTP错误处理
        if (e.response) {
            if (LOGIN_REDIRECT_CODES.includes(e.response.status)) {
                handleLoginRedirect(e.response.status, HttpErrors[e.response.status]);
            }
        } else if (e.request) {
            Toast.show('网络错误，请检查网络连接');
        }
        res = e?.response?.data;
    }
    return res;
};

export const getApi = async ({ api, params, success }) => {
    const response = await api(params);

    // 处理业务状态码跳转逻辑
    if (LOGIN_REDIRECT_CODES.includes(response?.status)) {
        handleLoginRedirect(response.status, response.msg);
    }

    if (response?.status === 1 || response?.status === 0) {
        success?.();
        return response.data;
    }
    Toast.show(response?.msg || 'error');
}

/**
 * 轮询指定接口直到满足条件或超时
 * @param {Function} apiFn - 调用接口的函数，需返回 Promise
 * @param {Function} checkFn - 判断结果是否满足条件的函数
 * @param {Object} options - 配置选项
 * @param {number} [options.interval=1000] - 轮询间隔（毫秒）
 * @param {number} [options.timeout=30000] - 超时时间（毫秒）
 * @param {Function} [options.onError] - 错误处理函数
 * @returns {Promise} - 返回一个 Promise，resolve 成功结果，reject 超时或错误
 */
export const pollApi = async (apiFn, checkFn, options = {}) => {
    const {
        interval = 1000,
        timeout = 30000,
        onError = err => console.error('Polling error:', err)
    } = options;

    let isTimeout = false;
    const timeoutId = setTimeout(() => {
        isTimeout = true;
    }, timeout);

    try {
        while (!isTimeout) {
            try {
                const result = await apiFn();

                // 处理业务状态码跳转逻辑
                if (LOGIN_REDIRECT_CODES.includes(result?.status)) {
                    handleLoginRedirect(result.status, result.msg);
                }

                if (result.status == 0 || result.status == 1 && result.data === 1) {
                    isTimeout = true;
                }
                if (checkFn(result)) {
                    return result;
                }
            } catch (err) {
                onError(err);
            }
            await new Promise(resolve => setTimeout(resolve, interval));
        }
        throw new Error('Polling timed out');
    } finally {
        clearTimeout(timeoutId);
    }
}